import classNames from "classnames"
import {kebabCase, startCase} from "lodash"
import React from "react"
import {useParams} from "react-router-dom"
import {useListReturnPath} from "../../hooks/useListReturnPath"
import {
	CheckPaymentComponent,
	getButtonsToShow,
	CheckActionButtons,
	isReturnWindowValid,
} from "../../components/CheckPayments/CheckPayment"
import {
	buildCheckPaymentCustomerMetaItems,
	CHECK_PAYMENT_DETAIL_INCLUDE,
	resolveCheckPaymentCustomers,
} from "../../components/CheckPayments/checkPaymentCustomers"
import TitleBar, {MetaItemProps} from "../../components/TitleBar/TitleBar"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {Account} from "../../resources/account"
import {CheckPayment, getCheckPayment} from "../../resources/checkPayments"
import {OkDocument} from "../../resources/common"
import {useAccessToken, useUserType} from "../../services/auth"
import MainSectionLayout from "../../containers/MainSection/MainSectionLayout"
import {CheckImageCard} from "../../components/CheckDeposits/CheckImageCard"
import {useCheckPaymentImages} from "../../hooks/useCheckPaymentImages"

function CheckPaymentPageContent({
	checkPaymentId,
	checkPaymentResponse,
	accessToken,
	listReturnPath,
	refresh,
}: {
	checkPaymentId: string
	checkPaymentResponse: OkDocument<CheckPayment>
	accessToken: string
	listReturnPath: string
	refresh: () => void
}) {
	const checkPayment = checkPaymentResponse.data
	const id = checkPayment.id
	const accountId = checkPayment.relationships.account?.data.id
	const resolvedCustomers = resolveCheckPaymentCustomers(checkPayment, checkPaymentResponse.included)
	const status = checkPayment.attributes.status
	const checkPaymentTransactionId = checkPayment.relationships.transaction?.data.id
	const userType = useUserType()
	const additionalVerificationStatus = checkPayment.attributes.additionalVerificationStatus

	const account =
		checkPaymentResponse.included &&
		(checkPaymentResponse.included.find(({type, id}) => type === "depositAccount" && id == accountId) as Account)

	const {frontPrintedImage, frontDepositedImage, backPrintedImage, backDepositedImage} = useCheckPaymentImages(
		accessToken,
		id
	)
	const originated = checkPayment.attributes.originated

	const customerMeta = buildCheckPaymentCustomerMetaItems(checkPayment, resolvedCustomers)

	return (
		<>
			<TitleBar
				title={`${startCase(checkPayment.type)} #${checkPaymentId}`}
				breadcrumbs={[{text: "Check Payments", path: listReturnPath}]}
				status={{
					text: startCase(checkPayment.attributes.status),
					className: classNames("check-payments-status", kebabCase(checkPayment.attributes.status)),
				}}
				meta={[
					...(accountId
						? [
								{
									text: "Account",
									icon: "bank-account--business-products",
									path: `/accounts/${accountId}`,
								} as MetaItemProps,
						  ]
						: []),
					...customerMeta,
					...(accountId && checkPaymentTransactionId
						? [
								{
									text: "Transaction",
									icon: "money-transfer--business-products",
									path: `/transaction/${accountId}/${checkPaymentTransactionId}`,
								} as MetaItemProps,
						  ]
						: []),
				]}
			>
				{checkPayment.attributes.returnCutoffTime && isReturnWindowValid(checkPayment.attributes.returnCutoffTime) ? (
					<CheckActionButtons
						checkPaymentId={checkPaymentId}
						refresh={refresh}
						buttons={getButtonsToShow(status, userType, additionalVerificationStatus)}
						disabled={false}
						title={""}
					/>
				) : null}
			</TitleBar>
			<MainSectionLayout
				fullWidth={true}
				outerWrapperClassname={classNames("received-payments-full-with-details")}
				renderAfter={
					<div className="check-image-card-section">
						<CheckImageCard
							imageVersionMode
							frontPrintedImage={frontPrintedImage}
							backPrintedImage={backPrintedImage}
							frontDepositedImage={frontDepositedImage}
							backDepositedImage={backDepositedImage}
							id={id}
							status={status}
							originated={originated}
						/>
					</div>
				}
			>
				<CheckPaymentComponent
					checkPaymentId={checkPaymentId}
					accessToken={accessToken}
					useDrawer={false}
					refresh={refresh}
					prefetchedCheckPayment={checkPaymentResponse}
					customers={resolvedCustomers}
					accounts={account ? [account] : undefined}
				/>
			</MainSectionLayout>
		</>
	)
}

export default function CheckPaymentPage() {
	const accessToken = useAccessToken()
	const {checkPaymentId = ""} = useParams<{checkPaymentId: string}>()
	const listReturnPath = useListReturnPath("/check-payments/")
	const [refreshToken, refresh] = useRefreshToken()
	const checkPaymentAsyncResultReq = useAsyncResult(
		() => getCheckPayment(accessToken, checkPaymentId, CHECK_PAYMENT_DETAIL_INCLUDE),
		[refreshToken, checkPaymentId]
	)

	return (
		<div className="check-payment-full-page">
			<AsyncResultComponent asyncResult={checkPaymentAsyncResultReq}>
				{({value: checkPaymentResponse}) => (
					<CheckPaymentPageContent
						checkPaymentId={checkPaymentId}
						checkPaymentResponse={checkPaymentResponse}
						accessToken={accessToken}
						listReturnPath={listReturnPath}
						refresh={refresh}
					/>
				)}
			</AsyncResultComponent>
		</div>
	)
}
